import { looksLikeQuestion } from "@/common/utils/json-ld.utils";

/**
 * Splits a listing page's Strapi `description` into the pieces the SEO block renders
 * separately (WHO-5534): the prose intro, the buyer questions, and the generated guide index.
 *
 * The copy is one markdown document written for a single column. The redesign folds the
 * intro behind "read more", turns the question-shaped headings into an accordion, and
 * renders the guide index as cards instead of a 37-item bullet list, so the document has to
 * be taken apart first. Everything stays in the output; nothing is dropped.
 */

export type ListingQuestion = {
  question: string;
  /** The answer as markdown, links intact. */
  answerMarkdown: string;
};

export type ListingGuideLink = {
  href: string;
  title: string;
};

export type ListingDescription = {
  /** Everything that is not a question or the guide index, as markdown. */
  introMarkdown: string;
  questions: ListingQuestion[];
  /** Links from the `<!-- guide-index -->` block on the bags root, in order. */
  guideIndex: ListingGuideLink[];
};

const GUIDE_INDEX_START = "<!-- guide-index:start -->";
const GUIDE_INDEX_END = "<!-- guide-index:end -->";
const GUIDE_LINK_RE = /^- \[([^\]]+)\]\(([^)\s]+)\)\s*$/;
const HEADING_RE = /^(#{2,4})\s+(.+?)\s*$/gm;
const TIGHT_HEADING_RE = /^(#{1,6})(?=[^#\s])/gm;

/**
 * Inserts the space CommonMark requires between `##` and the heading text.
 *
 * Several legacy Strapi pages were written as `##Heading`, which markdown-to-jsx renders as
 * a literal paragraph starting with hashes (the `/nl/meubels/banken` bug in WHO-5534). The
 * intent is unambiguous, so the space is added rather than the markup shown.
 */
export const normalizeMarkdownHeadings = (markdown: string): string =>
  markdown.replace(TIGHT_HEADING_RE, "$1 ");

const extractGuideIndex = (
  markdown: string
): { rest: string; guideIndex: ListingGuideLink[] } => {
  const start = markdown.indexOf(GUIDE_INDEX_START);
  const end = markdown.indexOf(GUIDE_INDEX_END);

  if (start === -1 || end === -1 || end < start) {
    return { rest: markdown, guideIndex: [] };
  }

  const block = markdown.slice(start + GUIDE_INDEX_START.length, end);
  const guideIndex: ListingGuideLink[] = [];

  for (const line of block.split("\n")) {
    const match = line.match(GUIDE_LINK_RE);

    if (match?.[1] && match[2]) {
      guideIndex.push({ title: match[1], href: match[2] });
    }
  }

  const rest =
    markdown.slice(0, start) + markdown.slice(end + GUIDE_INDEX_END.length);

  return { rest, guideIndex };
};

export const splitListingDescription = (
  markdown: string | null | undefined
): ListingDescription => {
  if (!markdown?.trim()) {
    return { introMarkdown: "", questions: [], guideIndex: [] };
  }

  const { rest, guideIndex } = extractGuideIndex(
    normalizeMarkdownHeadings(markdown)
  );

  const headings: { index: number; length: number; text: string }[] = [];

  for (const match of rest.matchAll(HEADING_RE)) {
    headings.push({
      index: match.index ?? 0,
      length: match[0].length,
      text: match[2] ?? "",
    });
  }

  const introParts: string[] = [];
  const questions: ListingQuestion[] = [];
  let cursor = 0;

  headings.forEach((heading, position) => {
    const next = headings[position + 1];
    const sectionEnd = next ? next.index : rest.length;

    if (!looksLikeQuestion(heading.text)) {
      return;
    }

    introParts.push(rest.slice(cursor, heading.index));

    const answerMarkdown = rest
      .slice(heading.index + heading.length, sectionEnd)
      .trim();

    if (answerMarkdown) {
      questions.push({ question: heading.text.trim(), answerMarkdown });
    }

    cursor = sectionEnd;
  });

  introParts.push(rest.slice(cursor));

  return {
    introMarkdown: introParts
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
    questions,
    guideIndex,
  };
};
